/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/start-templates/QaStartTemplatesTree.tsx
 * FILE_ID: JOBHUB-QA-START-TREE-001
 * PURPOSE: Location, template, and checklist checkboxes for Prepare QA.
 */
import { useEffect, useRef } from 'react'
import { useIsMdUp } from '@quotech/ui'
import type { QaTemplateDetail } from '../../../qa/qaTemplateTypes'
import type { QaTemplateLocationGroup } from '../templates/qaTemplateCatalog'
import {
  defaultSections,
  triState,
  type AreaNames,
  type StartSelection,
} from './qaStartTemplatesSelection'

const CHECK = 'h-4 w-4 shrink-0 rounded border-border text-primary focus:ring-primary'
const INPUT = 'w-full rounded-lg border border-border bg-background px-3 py-2 text-sm'

function CheckBox({
  checked,
  indeterminate,
  onChange,
  label,
}: {
  checked: boolean
  indeterminate?: boolean
  onChange: (on: boolean) => void
  label: string
}) {
  const ref = useRef<HTMLInputElement>(null)
  const isMdUp = useIsMdUp()
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = Boolean(indeterminate)
  }, [indeterminate])
  return (
    <label className={`flex min-w-0 items-center gap-2 ${isMdUp ? '' : 'min-h-[44px]'}`}>
      <input
        ref={ref}
        type="checkbox"
        className={CHECK}
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="min-w-0 truncate">{label}</span>
    </label>
  )
}

function sectionState(detail: QaTemplateDetail, sel: StartSelection) {
  const ids = defaultSections(detail)
  const chosen = ids.filter((id) => sel[detail.id]?.has(id)).length
  return triState(ids.length, chosen)
}

export function QaStartTemplatesTree({
  groups,
  detailsById,
  selection,
  areaNames,
  onAreaName,
  onToggleLocation,
  onToggleTemplate,
  onToggleSection,
}: {
  groups: QaTemplateLocationGroup[]
  detailsById: Record<string, QaTemplateDetail>
  selection: StartSelection
  areaNames: AreaNames
  onAreaName: (key: string, value: string) => void
  onToggleLocation: (group: QaTemplateLocationGroup, on: boolean) => void
  onToggleTemplate: (detail: QaTemplateDetail, on: boolean) => void
  onToggleSection: (templateId: string, sectionId: string) => void
}) {
  return (
    <div className="max-h-[45vh] space-y-2 overflow-y-auto">
      {groups.map((group) => {
        const details = group.templates
          .map((template) => detailsById[template.id])
          .filter((detail): detail is QaTemplateDetail => Boolean(detail))
        const totals = details.reduce((sum, detail) => sum + defaultSections(detail).length, 0)
        const chosen = details.reduce(
          (sum, detail) => sum + defaultSections(detail).filter((id) => selection[detail.id]?.has(id)).length,
          0,
        )
        const state = triState(totals, chosen)
        const named = details.some((detail) => (selection[detail.id]?.size ?? 0) > 0)
        return (
          <div key={group.location_type || 'other'} className="min-w-0">
            <CheckBox
              checked={state === 'all'}
              indeterminate={state === 'some'}
              onChange={(on) => onToggleLocation(group, on)}
              label={group.label}
            />
            {named && (
              <label className="mb-1 block min-w-0 pl-6 text-xs text-muted-foreground">
                Area name
                <input
                  aria-label={`Area name for ${group.label}`}
                  className={`${INPUT} mt-1`}
                  value={areaNames[group.location_type] ?? ''}
                  placeholder={group.label}
                  onChange={(event) => onAreaName(group.location_type, event.target.value)}
                />
              </label>
            )}
            <div className="space-y-1 pl-6">
              {details.map((detail) => {
                const templateState = sectionState(detail, selection)
                return (
                  <div key={detail.id}>
                    <CheckBox
                      checked={templateState === 'all'}
                      indeterminate={templateState === 'some'}
                      onChange={(on) => onToggleTemplate(detail, on)}
                      label={detail.name}
                    />
                    <div className="pl-6">
                      {detail.sections.map((section) => (
                        <CheckBox
                          key={section.id}
                          checked={selection[detail.id]?.has(section.id) ?? false}
                          onChange={() => onToggleSection(detail.id, section.id)}
                          label={section.title}
                        />
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}
